import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'

import { joinSession } from '@/api/join'
import { ApiError } from '@/api/client'
import { BrandMark } from '@/components/ui-kit/Brand'
import { ConnectionBadge } from '@/components/ConnectionBadge'
import { PhoneShell } from '@/components/PhoneShell'
import { useSessionStore } from '@/stores/session'
import { Link } from 'react-router'
import { cn } from '@/lib/utils'

/**
 * Beitritt zur Runde.
 *
 * Über den QR-Code kommt der Code schon in der Adresse mit, dann muss nur noch
 * der Spitzname eingetippt werden - in einer Klasse mit 30 Handys spart das
 * spürbar Zeit.
 */
export default function JoinPage() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { code: codeFromUrl } = useParams<{ code?: string }>()
    const join = useSessionStore((state) => state.join)

    const [code, setCode] = useState((codeFromUrl ?? '').toUpperCase())
    const [nickname, setNickname] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const submit = async (event: React.FormEvent) => {
        event.preventDefault()
        setBusy(true)
        setError(null)

        try {
            const response = await joinSession(code, nickname)

            join({
                token: response.token,
                nickname: response.nickname,
                joinCode: code.trim().toUpperCase(),
                participantId: response.participantId,
            })

            void navigate('/runde')
        } catch (caught) {
            setError(messageFor(caught, t))
        } finally {
            setBusy(false)
        }
    }

    return (
        <PhoneShell>
            <header className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                    <BrandMark size={36} />
                    <div>
                        <div className="text-lg leading-tight font-extrabold">{t('app.name')}</div>
                        <div className="text-[10px] tracking-[.1em] text-wb-dim uppercase">
                            {t('join.role')}
                        </div>
                    </div>
                </div>
                <ConnectionBadge />
            </header>

            <form onSubmit={(event) => void submit(event)} className="flex flex-1 flex-col pt-10">
                <h1 className="wb-display text-4xl">{t('join.title')}</h1>
                <p className="mt-2 text-wb-dim">{t('join.intro')}</p>

                <label className="mt-8 block">
                    <span className="text-[11px] font-bold tracking-[.08em] text-wb-dim uppercase">
                        {t('join.code')}
                    </span>
                    <input
                        value={code}
                        onChange={(event) => setCode(event.target.value.toUpperCase().slice(0, 6))}
                        placeholder={t('join.codePlaceholder')}
                        autoComplete="off"
                        autoCapitalize="characters"
                        // inputMode text statt numeric: der Code enthält Buchstaben.
                        inputMode="text"
                        className="mt-2 w-full rounded-2xl border-2 border-wb-border bg-wb-panel px-4 py-4 text-center wb-num text-3xl font-extrabold tracking-[.3em] outline-none focus:border-wb-accent"
                    />
                </label>

                <label className="mt-5 block">
                    <span className="text-[11px] font-bold tracking-[.08em] text-wb-dim uppercase">
                        {t('join.nickname')}
                    </span>
                    <input
                        value={nickname}
                        onChange={(event) => setNickname(event.target.value.slice(0, 20))}
                        placeholder={t('join.nicknamePlaceholder')}
                        autoComplete="off"
                        className="mt-2 w-full rounded-2xl border-2 border-wb-border bg-wb-panel px-4 py-4 text-lg font-bold outline-none focus:border-wb-accent"
                    />
                    <span className="mt-2 block text-xs text-wb-muted">
                        {t('join.nicknameHint')}
                    </span>
                </label>

                {error !== null && (
                    <p
                        role="alert"
                        className="mt-5 rounded-xl border border-wb-loss/40 bg-wb-loss/10 px-4 py-3 text-sm text-wb-loss"
                    >
                        {error}
                    </p>
                )}

                <div className="mt-auto" />

                <button
                    type="submit"
                    disabled={busy || code.length < 6 || nickname.trim().length < 2}
                    className={cn(
                        'w-full rounded-2xl bg-wb-accent px-6 py-4.5 text-lg font-extrabold text-[#0a0d14]',
                        'shadow-[0_6px_0_var(--color-wb-accent-lo)] transition',
                        'active:translate-y-[3px] active:shadow-[0_3px_0_var(--color-wb-accent-lo)]',
                        'disabled:opacity-40 disabled:shadow-none',
                    )}
                >
                    {busy ? t('join.submitting') : t('join.submit')}
                </button>

                <footer className="mt-3 flex justify-center gap-4 text-[11px] text-wb-muted">
                    <Link to="/datenschutz" className="underline">
                        {t('legal.privacy.title')}
                    </Link>
                    <Link to="/impressum" className="underline">
                        {t('legal.imprint.title')}
                    </Link>
                </footer>
            </form>
        </PhoneShell>
    )
}

function messageFor(caught: unknown, t: (key: string) => string): string {
    if (caught instanceof ApiError) {
        if (caught.status === 404) {
            return t('join.errors.notFound')
        }

        if (caught.status === 422) {
            return caught.fieldError('code') ?? caught.fieldError('nickname') ?? caught.message
        }

        return caught.message
    }

    // Kein HTTP-Status heißt: die Anfrage kam gar nicht erst durch.
    return t('join.errors.offline')
}
